import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import classnames from 'classnames';
import { forwardRef, useEffect, useId, useImperativeHandle, useRef, useState, } from 'react';
import { Button } from './button.js';
import { IconChevronDown } from './icons/index.js';
import { useTooltip } from './tooltip.js';
export const MenuButton = (props) => {
    const { children, danger, separator, tooltipOptions, className, disabled, ...rest } = props;
    const classes = classnames(className, 'pds-menu-item', {
        'pds-menu-item-danger': danger,
    });
    const tooltip = useTooltip(tooltipOptions);
    return (_jsxs("li", { role: "none", className: classnames({ 'pds-menu-separator': separator }), children: [tooltip?.element, _jsx(Button, { role: "menuitem", type: "button", className: classes, tabIndex: -1, disabled: disabled, ...tooltip?.targetProps, ...rest, children: children })] }));
};
export const MenuLink = (props) => {
    const { children, danger = false, separator = false, tooltipOptions, className, ...rest } = props;
    const classes = classnames(className, 'pds-menu-item', {
        'pds-menu-item-danger': danger,
    });
    const tooltip = useTooltip(tooltipOptions);
    return (_jsxs("li", { role: "none", className: classnames({ 'pds-menu-separator': separator }), children: [tooltip?.element, _jsx("a", { role: "menuitem", className: classes, tabIndex: -1, ...tooltip?.targetProps, ...rest, children: children })] }));
};
export const Menu = forwardRef(function Menu({ children, disabled, position = 'left', buttonVariant, buttonSize, buttonLabel, button: buttonProps = {}, }, ref) {
    const uid = useId();
    const [open, setOpen] = useState(false);
    const [faded, setFaded] = useState(true);
    const buttonRef = useRef(null);
    const listRef = useRef(null);
    const items = useRef([]);
    const index = useRef(0);
    // The button is first rendered with tabindex -1 to prevent it from being
    // focused when pressing shift-tab. Once focus has changed, we can remove the
    // attribute.
    useEffect(() => {
        if (open) {
            buttonRef.current?.setAttribute('aria-hidden', 'true');
            buttonRef.current?.setAttribute('tabindex', '-1');
        }
        else {
            setTimeout(() => {
                buttonRef.current?.removeAttribute('aria-hidden');
                buttonRef.current?.removeAttribute('tabindex');
            }, 0);
        }
    }, [open]);
    useEffect(() => {
        items.current = Array.from(listRef.current?.querySelectorAll('a,button') || []);
    });
    useEffect(() => {
        if (open)
            focus();
    }, [open]);
    useEffect(() => {
        function onClick(event) {
            if (open) {
                hide();
                return;
            }
            // Never show when disabled - sometimes SVG icons can still be clicked
            // even when the button is disabled.
            if (disabled)
                return;
            // Is the click within the button?
            if (!buttonRef.current?.contains(event.target))
                return;
            index.current = 0;
            show();
        }
        document.addEventListener('click', onClick);
        return () => {
            document.removeEventListener('click', onClick);
        };
    }, [disabled, open]);
    const show = () => {
        setOpen(true);
        setFaded(false);
    };
    const hide = () => {
        setOpen(false);
    };
    const focus = () => {
        const count = items.current.length;
        // Adjust for an out of bounds index
        if (index.current === -1) {
            index.current = count - 1;
        }
        else if (index.current >= count) {
            index.current = 0;
        }
        items.current[index.current]?.focus();
    };
    useImperativeHandle(ref, () => ({ show }));
    const onMenuKeyDown = (event) => {
        const count = items.current.length;
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                index.current++;
                focus();
                break;
            case 'ArrowUp':
                event.preventDefault();
                index.current--;
                focus();
                break;
            case 'Home':
                event.preventDefault();
                index.current = 0;
                focus();
                break;
            case 'End':
                event.preventDefault();
                index.current = count - 1;
                focus();
                break;
            case 'Tab':
                hide();
                break;
            case 'Escape':
                // When the dropdown is open, pressing Escape should close it but not
                // cause a dialog to close.
                event.stopPropagation();
                event.preventDefault();
                setTimeout(() => {
                    buttonRef.current?.focus();
                }, 0);
                hide();
                break;
        }
    };
    const onButtonKeyDown = (event) => {
        switch (event.key) {
            case 'ArrowDown':
                event.preventDefault();
                index.current = 0;
                show();
                break;
            case 'ArrowUp':
                event.preventDefault();
                index.current = -1;
                show();
                break;
        }
    };
    const onAnimationEnd = (event) => {
        if (event.animationName === 'fadeout') {
            setFaded(true);
        }
    };
    function button() {
        const { className, ...rest } = buttonProps;
        const classes = classnames(className, { 'pds-button-selected': open });
        return (_jsxs(Button, { id: `${uid}button`, ref: buttonRef, variant: buttonVariant, size: buttonSize, className: classes, onKeyDown: onButtonKeyDown, "aria-haspopup": "true", "aria-expanded": open ? 'true' : 'false', ...rest, "aria-controls": `${uid}menu`, disabled: disabled, children: [buttonLabel, _jsx(IconChevronDown, { "aria-hidden": "true" })] }));
    }
    function onMenuMouseMove(event) {
        const element = event.target;
        if (element.matches('a,button')) {
            const newIndex = items.current.indexOf(element);
            if (newIndex !== -1) {
                index.current = newIndex;
                focus();
            }
        }
    }
    return (_jsxs("div", { className: "pds-menu-container", children: [button(), !faded && (_jsx("div", { className: classnames('pds-menu-backdrop', {
                    'pds-fadeout': !open,
                }), onAnimationEnd: onAnimationEnd, children: _jsx("div", { className: `pds-menu pds-menu-${position}`, children: _jsx("ul", { onMouseMove: onMenuMouseMove, ref: listRef, role: "menu", id: `${uid}menu`, onKeyDown: onMenuKeyDown, "aria-labelledby": `${uid}button`, children: children }) }) }))] }));
});
